/**
 * ts-main-style Sidebar for fleet-doctor.
 * Renders fleet-doctor's dynamic role-based menu tree with ts-main's dark navy visual design.
 * Uses lucide-react icons instead of Metronic SVG icons.
 */
import { useState, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../app/modules/auth'
import { getSidebarStaticMenus, buildMenusFromTree } from '../../myMenus'
import { hasAnyMenuTreeCanView } from '../../app/shared/utils/menuTree'
import { resolveIcon } from './iconMap'
import type { MenuGroup, MenuItem } from '../../_metronic/layout/components/sidebar/sidebar-menu/types'
import { Anchor } from 'lucide-react'
import { filterMenuByPermissions } from '../../_metronic/layout/components/sidebar/sidebar-menu/sidebarPermissionFilter'

type SidebarSectionKey = 'home' | 'operations' | 'technical' | 'crew' | 'qhse' | 'admin' | 'pms' | 'accounts'

type SectionMeta = {
  title: string
  subtitle: string
  rootNames: string[]
  groupLabel: string
  renderRootsAsChildren?: boolean
  iconName: string
}

const SECTION_META: Record<SidebarSectionKey, SectionMeta> = {
  home: {
    title: 'Fleet Command Center',
    subtitle: 'Operational Intelligence Platform',
    rootNames: ['Home'],
    groupLabel: '',
    iconName: 'Home',
  },
  operations: {
    title: 'Operations',
    subtitle: 'Voyage & Port Ops',
    rootNames: ['Operations'],
    groupLabel: 'Operations',
    renderRootsAsChildren: true,
    iconName: 'Operations',
  },
  technical: {
    title: 'Technical',
    subtitle: 'Technical - PMS & Procurement',
    rootNames: ['Technical'],
    groupLabel: 'Technical',
    renderRootsAsChildren: true,
    iconName: 'Technical',
  },
  crew: {
    title: 'Crew',
    subtitle: 'Crew Management',
    rootNames: ['Crewing', 'Crewingtest'],
    groupLabel: 'Crew',
    renderRootsAsChildren: true,
    iconName: 'Crewing',
  },
  pms: {
    title: 'PMS',
    subtitle: 'Planned Maintenance System',
    rootNames: ['PMS'],
    groupLabel: 'PMS',
    renderRootsAsChildren: true,
    iconName: 'PMS',
  },
  qhse: {
    title: 'QHSE',
    subtitle: 'Quality, Health, Safety, Env.',
    rootNames: ['QHSE'],
    groupLabel: 'QHSE',
    renderRootsAsChildren: true,
    iconName: 'QHSE',
  },
  admin: {
    title: 'Admin',
    subtitle: 'Fleet Administration',
    rootNames: ['Admin'],
    groupLabel: 'Admin',
    renderRootsAsChildren: true,
    iconName: 'Admin',
  },
  accounts: {
    title: 'Accounts',
    subtitle: 'Accounts Dashboard',
    rootNames: ['Accounts'],
    groupLabel: 'Accounts',
    renderRootsAsChildren: true,
    iconName: 'Accounts',
  },
}

const getSectionKeyFromPath = (pathname: string): SidebarSectionKey => {
  const path = pathname.toLowerCase()
  if (path.startsWith('/technical') || path.startsWith('/procurement')) return 'technical'
  if (path.startsWith('/operations')) return 'operations'
  if (path.startsWith('/crewing') || path.startsWith('/crewingtest')) return 'crew'
  if (path.startsWith('/qhse')) return 'qhse'
  if (path.startsWith('/manage')) return 'admin'
  if (path.startsWith('/accounts')) return 'accounts'
  return 'home'
}

const findMenuItemByName = (groups: MenuGroup[], name: string): MenuItem | null => {
  const search = (items: MenuItem[]): MenuItem | null => {
    for (const item of items) {
      if (item.name === name) return item
      if (item.submenu?.length) {
        const found = search(item.submenu)
        if (found) return found
      }
    }
    return null
  }

  for (const group of groups) {
    const found = search(group.menu || [])
    if (found) return found
  }
  return null
}

// Per-module dashboard landing routes. Used to surface a "Dashboard" entry at the
// top of each module's sidebar section. 'home' is itself the dashboard, so excluded.
const SECTION_DASHBOARD_ROUTES: Partial<Record<SidebarSectionKey, string>> = {
  operations: '/operations/dashboard',
  technical: '/technical/pms/dashboard',
  crew: '/crewing/dashboard',
  qhse: '/qhse/dashboard',
  admin: '/manage/dashboard',
  accounts: '/accounts/dashboard',
  pms: '/technical/pms/dashboard',
}

const buildSectionGroups = (groups: MenuGroup[], sectionKey: SidebarSectionKey): MenuGroup[] => {
  const meta = SECTION_META[sectionKey]
  const collected: MenuItem[] = []

  for (const rootName of meta.rootNames) {
    const root = findMenuItemByName(groups, rootName)
    if (!root) continue

    if (sectionKey === 'home') {
      collected.push(root)
      continue
    }

    if (root.submenu?.length) {
      collected.push(...root.submenu)
      continue
    }

    collected.push(root)
  }

  if (!collected.length) return []

  // Ensure a Dashboard entry sits at the top of every module section.
  // De-dupe any existing top-level Dashboard (e.g. Operations, Accounts) to avoid repeats.
  const dashboardRoute = SECTION_DASHBOARD_ROUTES[sectionKey]
  let sectionItems = collected
  if (dashboardRoute) {
    const withoutDashboard = collected.filter(
      (item) => (item.name || '').trim().toLowerCase() !== 'dashboard'
    )
    const dashboardItem: MenuItem = {
      name: 'Dashboard',
      route: dashboardRoute,
      icon: 'Dashboard',
      svg_location: 'menu',
    }
    sectionItems = [dashboardItem, ...withoutDashboard]
  }

  return [{ name: meta.groupLabel, menu: sectionItems }]
}

/* ─── Icon wrapper: renders a lucide icon for a menu item ─── */
function MenuIcon({ itemName, moduleName, className = 'w-5 h-5' }: { itemName: string; moduleName?: string; className?: string }) {
  const Icon = resolveIcon(itemName, moduleName)
  return <Icon className={className} />
}

/* ─── Single menu item (no submenu) ─── */
function SidebarLeafItem({ item, moduleName, isActive, onNavigate }: { item: MenuItem; moduleName: string; isActive: boolean; onNavigate?: () => void }) {
  return (
    <Link
      to={item.route}
      onClick={onNavigate}
      className={`sidebar-item sidebar-leaf flex items-center gap-2 px-2 py-2 rounded-xl text-[12px] font-medium ${
        isActive
          ? 'active text-white'
          : 'text-slate-300 hover:text-white hover:bg-white/5'
      }`}
    >
      <MenuIcon itemName={item.name} moduleName={moduleName} className="w-[16px] h-[16px]" />
      <span className="truncate leading-none">{item.name}</span>
    </Link>
  )
}

/* ─── Menu item with submenu (expandable) ─── */
function SidebarGroupItem({
  item,
  moduleName,
  activeRoutes,
  depth = 0,
  onNavigate,
}: {
  item: MenuItem
  moduleName: string
  activeRoutes: string[]
  depth?: number
  onNavigate?: () => void
}) {
  const location = useLocation()
  const isCrewSection = location.pathname.startsWith('/crewing') || location.pathname.startsWith('/CrewingTest')

  const hasActiveChild = item.submenu?.some((sub) => {
    if (sub.route === location.pathname) return true
    return activeRoutes.includes(sub.route || '')
  })

  const isCurrentRouteActive = item.route === location.pathname
  const hasChildren = !!item.submenu?.length
  const parentIsHot = hasActiveChild
  const parentAccentClass = parentIsHot ? 'text-[#f59e0b]' : 'text-[#f4a300]'
  const parentLabel = hasChildren && isCrewSection ? item.name.replace(/^Crew\s+/i, '') : item.name

  return (
    <div>
      {hasChildren ? (
        <div className={`sidebar-group flex items-center gap-2 px-3.5 py-1.5 mt-2 mb-1 text-[11px] font-extrabold uppercase tracking-[0.14em] ${parentAccentClass}`}>
          {depth === 0 && <MenuIcon itemName={item.name} moduleName={moduleName} className="w-[18px] h-[18px] text-current" />}
          {depth > 0 && <span className="w-2 h-2 rounded-full bg-current/60 flex-shrink-0" />}
          {/* removed this classname - truncate */}
          <span className=" flex-1">{parentLabel}</span>
        </div>
      ) : (
        <button
          onClick={() => {
            if (item.route) onNavigate?.()
          }}
          className={`sidebar-item sidebar-group flex items-center gap-3 px-3.5 py-2 rounded-xl text-[14px] font-medium w-full text-left ${
            hasActiveChild || isCurrentRouteActive
              ? 'active text-white'
              : 'text-slate-300 hover:text-white hover:bg-white/5'
          }`}
        >
          {depth === 0 && <MenuIcon itemName={item.name} moduleName={moduleName} className="w-[18px] h-[18px]" />}
          {depth > 0 && <span className="w-2 h-2 rounded-full bg-slate-400/60 flex-shrink-0" />}
          <span className="truncate flex-1">{item.name}</span>
        </button>
      )}

      {item.submenu && (
        <div className="ml-0 mt-0.5 space-y-1 pl-0">
          {item.submenu.map((subItem, subIdx) => {
            if (subItem.submenu && subItem.submenu.length > 0) {
              return (
                <SidebarGroupItem
                  key={subIdx}
                  item={subItem}
                  moduleName={moduleName}
                  activeRoutes={activeRoutes}
                  depth={depth + 1}
                  onNavigate={onNavigate}
                />
              )
            }
            return (
              <SidebarLeafItem
                key={subIdx}
                item={subItem}
                moduleName={moduleName}
                isActive={subItem.route === location.pathname}
                onNavigate={onNavigate}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ─── Main Sidebar component ─── */
export default function MaritimeSidebar() {
  const { currentUser, menuTree, hasPermission, isSuperAdmin, permissionsLoaded } = useAuth()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)     // mobile overlay toggle (starts closed)

  const roleId = currentUser?.role?.id || 0
  const roleType = String(currentUser?.role?.roleType || (currentUser as any)?.role?.roleName || '').toUpperCase()
  const roleIdByType: Record<string, number> = {
    SUPER_ADMIN: 1,
    COMPANY_ADMIN: 2,
    COMPANY_GROUP_ADMIN: 5,
    SHORE: 2,
    CREW: 4,
    OPERATOR: 6,
  }
  const effectiveRoleId = roleType && roleIdByType[roleType] ? roleIdByType[roleType] : roleId
  const rankId = currentUser?.rank?.id || undefined
  const hasActiveTree = hasAnyMenuTreeCanView(menuTree)

  const hasAccountsModule = (groups: MenuGroup[]) =>
    groups.some((group) => (group?.menu || []).some((item: any) => item?.name === 'Accounts'))

  const mergeAccountsIfMissing = useCallback((groups: MenuGroup[]): MenuGroup[] => {
    if (effectiveRoleId !== 5 || hasAccountsModule(groups)) return groups
    const staticMenus = getSidebarStaticMenus(effectiveRoleId, rankId)
    const accountsItem = staticMenus.flatMap((g) => g.menu || []).find((item) => item?.name === 'Accounts')
    if (!accountsItem) return groups
    if (!groups.length) return [{ name: '', menu: [accountsItem] }]
    const targetIndex = groups.findIndex((g) => g?.name === '')
    if (targetIndex === -1) return [...groups, { name: '', menu: [accountsItem] }]
    return groups.map((g, idx) => (idx === targetIndex ? { ...g, menu: [...(g.menu || []), accountsItem] } : g))
  }, [effectiveRoleId, rankId])

  const menus = useMemo(() => {
    const baseMenus =
      !isSuperAdmin && hasActiveTree && menuTree?.length
        ? buildMenusFromTree(menuTree)
        : getSidebarStaticMenus(effectiveRoleId, rankId)

    const mergedMenus = mergeAccountsIfMissing(baseMenus)
    if (isSuperAdmin) return mergedMenus
    if (!permissionsLoaded) return []

    const canRead = (permissionCode: string) => hasPermission(permissionCode)

    return mergedMenus
      .map((group) => ({
        ...group,
        menu: filterMenuByPermissions(group.menu, canRead),
      }))
      .filter((group) => Array.isArray(group.menu) && group.menu.length > 0)
  }, [
    effectiveRoleId,
    hasActiveTree,
    hasPermission,
    isSuperAdmin,
    menuTree,
    mergeAccountsIfMissing,
    permissionsLoaded,
    rankId,
  ])

  // Collect all routes from menus for active state detection
  const getAllRoutes = (groups: MenuGroup[]): string[] => {
    const routes: string[] = []
    const traverse = (item: MenuItem) => {
      if (item.route) routes.push(item.route)
      if (item.submenu) item.submenu.forEach(traverse)
    }
    groups.forEach((g) => g.menu.forEach(traverse))
    return routes
  }

  const sectionKey = getSectionKeyFromPath(location.pathname)
  const sectionMeta = SECTION_META[sectionKey]
  const sectionMenus = useMemo(() => {
    return buildSectionGroups(menus, sectionKey)
  }, [menus, sectionKey])
  const sectionRoutes = getAllRoutes(sectionMenus)

  // Mobile sidebar toggle
  const toggleMobile = () => setMobileOpen(!mobileOpen)

  return (
    <>
      {/* Mobile overlay — only shown when mobile menu is open */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button (visible only on small screens) */}
      <button
        onClick={toggleMobile}
        className="fixed top-3 left-3 z-50 lg:hidden p-2 rounded-lg bg-white shadow-md border border-slate-200"
      >
        <Anchor className="w-5 h-5 text-slate-700" />
      </button>

      {/* Sidebar panel — always visible on desktop (lg+), overlay on mobile */}
      <aside
        className={`w-[264px] bg-[linear-gradient(180deg,#0e1728_0%,#111b31_45%,#0b1323_100%)] flex-shrink-0 flex flex-col transition-all duration-300 h-full z-50 overflow-hidden border-r border-white/5 shadow-[0_0_40px_rgba(2,6,23,0.28)] ${
          mobileOpen ? 'fixed inset-y-0 left-0 translate-x-0' : 'fixed inset-y-0 left-0 -translate-x-full lg:relative lg:translate-x-0'
        }`}
      >
        {/* Logo area */}
        <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-white/10">
          <div className="w-11 h-11 rounded-2xl bg-[linear-gradient(180deg,rgba(255,255,255,0.12),rgba(255,255,255,0.05))] flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] ring-1 ring-white/10">
            <MenuIcon itemName={sectionMeta.iconName} className="w-5.5 h-5.5 text-sky-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-semibold text-[15px] tracking-tight truncate leading-tight">{sectionMeta.title}</h1>
            <p className="text-slate-400 text-[11px] font-medium leading-tight">{sectionMeta.subtitle}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto min-h-0">
          {sectionMenus.map((menuGroup, groupIdx) => (
            <div key={groupIdx}>
              {/* Group separator label (if group has a name) */}
              {menuGroup.name && (
                <div className="px-3 pt-4 pb-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-amber-500/95">
                  {menuGroup.name} - Module
                </div>
              )}
              {menuGroup.menu.map((menuItem, itemIdx) => {
                if (menuItem.submenu && menuItem.submenu.length > 0) {
                  return (
                    <SidebarGroupItem
                      key={itemIdx}
                      item={menuItem}
                      moduleName={menuItem.name}
                      activeRoutes={sectionRoutes}
                      depth={0}
                      onNavigate={() => setMobileOpen(false)}
                    />
                  )
                }
                return (
                  <SidebarLeafItem
                    key={itemIdx}
                    item={menuItem}
                    moduleName={menuItem.name}
                    isActive={menuItem.route === location.pathname}
                    onNavigate={() => setMobileOpen(false)}
                  />
                )
              })}
            </div>
          ))}
        </nav>

        {/* Conversations / footer card */}
        {/* <div className="px-3.5 pb-4 pt-2">
          <div className="rounded-[20px] border border-white/10 bg-[rgba(15,23,42,0.78)] shadow-[0_16px_40px_rgba(0,0,0,0.28)] backdrop-blur-sm overflow-hidden">
            <div className="px-4 pt-4 pb-3 border-b border-white/10">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[14px] font-semibold text-white">Previous Conversations</h3>
                <button type="button" className="text-[11px] font-medium text-sky-300 hover:text-sky-200 transition-colors">View all</button>
              </div>
            </div>

            <div className="px-4 py-3 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full border border-amber-400/40 bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] leading-snug text-slate-100">Show overdue PMS jobs MT Oceanic</div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400">
                    <Clock3 className="w-3 h-3" />
                    <span>Today, 09:30 AM</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full border border-sky-400/40 bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-3.5 h-3.5 text-sky-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] leading-snug text-slate-100">Why compliance dropped?</div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400">
                    <Clock3 className="w-3 h-3" />
                    <span>Yesterday, 04:15 PM</span>
                  </div>
                </div>
              </div>

              <button type="button" className="pt-1 text-left text-[11px] font-medium text-sky-300 hover:text-sky-200 transition-colors">
                View all conversations &nbsp;›
              </button>
            </div>
          </div>
        </div> */}
      </aside>
    </>
  )
}
